import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Play, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { launchLessonIds } from "@/lib/launch/launchExperienceRoster";
import { getLumiLessonAudioTexts } from "@/data/lumiLessonAudioManifest";
import { LUMI_VOICE_ID } from "@/hooks/useLumiVoice";
import { getErrorMessage } from "@/lib/errors";
import type { AnyRow } from "@/lib/rowTypes";

interface MomentRow {
  moduleId: string;
  momentId: string;
  instructor: string;
  instructorLabel?: string;
  trigger: string;
  priority: string;
  transcript: string;
  conceptTags: string[];
  voiceId: string;
  modelId: string;
  status: "ready" | "missing" | "failed" | "stale" | "generating" | "pending";
  audioPath: string | null;
  errorMessage: string | null;
  updatedAt: string | null;
}

interface StatusSummary {
  total: number;
  generated: number;
  missing: number;
  failed: number;
  stale: number;
  generating: number;
}

const STATUS_COLOR: Record<string, string> = {
  ready: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  missing: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  failed: "bg-red-500/20 text-red-300 border-red-500/40",
  stale: "bg-orange-500/20 text-orange-300 border-orange-500/40",
  generating: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  pending: "bg-muted text-muted-foreground border-border",
};

export default function AdminInstructorVoice() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [summary, setSummary] = useState<StatusSummary | null>(null);
  const [moments, setMoments] = useState<MomentRow[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("instructor-voice", {
        body: { action: "status" },
      });
      if (error) throw error;
      setSummary((data).summary);
      setMoments((data).moments ?? []);
    } catch (e: unknown) {
      toast({ title: "Failed to load voice status", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  const generateOne = useCallback(async (momentId: string, force = false) => {
    setBusy(momentId);
    try {
      const { data, error } = await supabase.functions.invoke("instructor-voice", {
        body: { action: "generate", moment_id: momentId, force },
      });
      if (error) throw error;
      const status = (data)?.status;
      if (status === "failed") {
        toast({ title: `Generation failed: ${momentId}`, description: (data)?.error ?? "Unknown error", variant: "destructive" });
      } else {
        toast({ title: `Generated ${momentId}`, description: status === "cached" ? "Already cached" : "Audio ready" });
      }
      await load();
    } catch (e: unknown) {
      toast({ title: "Generate failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }, [load, toast]);

  const generateMissing = useCallback(async () => {
    setBusy("__all_missing__");
    try {
      const { data, error } = await supabase.functions.invoke("instructor-voice", {
        body: { action: "generate_all" },
      });
      if (error) throw error;
      const results = (data)?.results ?? [];
      const failed = results.filter((r: AnyRow) => r.status === "failed");
      toast({
        title: failed.length ? `Generated with ${failed.length} failures` : "All beta voice lines ready",
        description: failed.length ? failed.map((f: AnyRow) => f.momentId).join(", ") : `${results.length} moments processed`,
        variant: failed.length ? "destructive" : "default",
      });
      await load();
    } catch (e: unknown) {
      toast({ title: "Generate-all failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }, [load, toast]);

  const regenerateAll = useCallback(async () => {
    if (!confirm("Regenerate ALL 9 beta voice lines? This will consume ElevenLabs credits.")) return;
    setBusy("__regen_all__");
    try {
      const { data, error } = await supabase.functions.invoke("instructor-voice", {
        body: { action: "regenerate_all" },
      });
      if (error) throw error;
      const results = (data)?.results ?? [];
      toast({ title: "Regenerated all beta voice lines", description: `${results.length} moments processed` });
      await load();
    } catch (e: unknown) {
      toast({ title: "Regenerate failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    } finally {
      setBusy(null);
    }
  }, [load, toast]);

  const preview = useCallback(async (momentId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("instructor-voice", {
        body: { action: "preview", moment_id: momentId },
      });
      if (error) throw error;
      const url = (data)?.url as string | null;
      if (!url) {
        toast({ title: "No audio yet", description: "Generate this moment first.", variant: "destructive" });
        return;
      }
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      const audio = new Audio(url);
      audioRef.current = audio;
      setPlayingId(momentId);
      audio.addEventListener("ended", () => setPlayingId((p) => (p === momentId ? null : p)));
      await audio.play();
    } catch (e: unknown) {
      toast({ title: "Preview failed", description: getErrorMessage(e) ?? "Try again", variant: "destructive" });
    }
  }, [toast]);

  const beta = useMemo(() => {
    if (!summary) return null;
    const ready = summary.generated === summary.total && summary.failed === 0 && summary.stale === 0 && summary.missing === 0;
    return { ready };
  }, [summary]);

  const grouped = useMemo(() => {
    const map = new Map<string, MomentRow[]>();
    for (const m of moments) {
      if (!map.has(m.moduleId)) map.set(m.moduleId, []);
      map.get(m.moduleId)!.push(m);
    }
    return Array.from(map.entries());
  }, [moments]);

  // ---- Lumi seeding (admin-only prewarm) ----
  const [lumiSeeding, setLumiSeeding] = useState(false);
  const [lumiProgress, setLumiProgress] = useState<{ done: number; total: number; created: number; cached: number; failed: number } | null>(null);

  const seedLumiAudio = useCallback(async () => {
    const jobs: Array<{ lessonId: string; text: string }> = [];
    const seen = new Set<string>();
    for (const lessonId of launchLessonIds) {
      for (const text of getLumiLessonAudioTexts(lessonId)) {
        const key = `${lessonId}::${text}`;
        if (seen.has(key)) continue;
        seen.add(key);
        jobs.push({ lessonId, text });
      }
    }
    if (!jobs.length) {
      toast({ title: "No Lumi lines to seed", description: "Manifest is empty." });
      return;
    }
    if (!confirm(`Seed ${jobs.length} Lumi audio lines with voice ${LUMI_VOICE_ID}? Missing files will be generated via ElevenLabs once.`)) return;

    setLumiSeeding(true);
    let done = 0, created = 0, cached = 0, failed = 0;
    setLumiProgress({ done, total: jobs.length, created, cached, failed });
    for (const job of jobs) {
      try {
        const { data, error } = await supabase.functions.invoke("lumi-voice", {
          body: { text: job.text, lesson_id: job.lessonId, seed: true },
        });
        if (error) {
          failed++;
        } else if ((data)?.cached) {
          cached++;
        } else if ((data)?.url) {
          created++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
      done++;
      setLumiProgress({ done, total: jobs.length, created, cached, failed });
    }
    setLumiSeeding(false);
    toast({
      title: "Lumi seeding complete",
      description: `${created} generated · ${cached} already cached · ${failed} failed`,
      variant: failed ? "destructive" : "default",
    });
  }, [toast]);

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold">Instructor Voice — Beta Cache</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate, preview, and verify the 9 approved Lunimar Collective voice lines before beta launch.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading || !!busy}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={generateMissing} disabled={!!busy}>
            {busy === "__all_missing__" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Generate Missing
          </Button>
          <Button variant="destructive" onClick={regenerateAll} disabled={!!busy}>
            {busy === "__regen_all__" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Regenerate All
          </Button>
        </div>
      </div>

      {summary && (
        <Card className={beta?.ready ? "border-emerald-500/40" : "border-amber-500/40"}>
          <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {beta?.ready ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              )}
              <span className="font-semibold">
                {beta?.ready ? "Beta audio-ready" : "Beta NOT audio-ready"}
              </span>
            </div>
            <div className="flex gap-3 text-sm">
              <span><strong>{summary.generated}</strong>/{summary.total} generated</span>
              <span className="text-amber-300">{summary.missing} missing</span>
              <span className="text-red-300">{summary.failed} failed</span>
              <span className="text-orange-300">{summary.stale} stale</span>
              {summary.generating > 0 && <span className="text-cyan-300">{summary.generating} generating</span>}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-cyan-500/30">
        <CardHeader>
          <CardTitle className="text-base">Lumi Voice — Beta Prewarm</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Generates and stores audio for every approved Lumi voice moment across all beta lessons
            using voice <code className="text-xs">{LUMI_VOICE_ID}</code>. Lines already cached for this
            voice are skipped. Learners never trigger generation — only this seeding pass does.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={seedLumiAudio} disabled={lumiSeeding}>
              {lumiSeeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Seed All Lumi Beta Audio
            </Button>
            {lumiProgress && (
              <span className="text-sm text-muted-foreground">
                {lumiProgress.done}/{lumiProgress.total} · {lumiProgress.created} new · {lumiProgress.cached} cached · {lumiProgress.failed} failed
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {loading && !moments.length && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {grouped.map(([moduleId, rows]) => (
        <Card key={moduleId}>
          <CardHeader>
            <CardTitle className="font-heading capitalize">{moduleId.replace(/_/g, " ")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {rows.map((m) => (
              <div key={m.momentId} className="rounded-lg border border-border/60 p-4 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm">{m.momentId}</span>
                      <Badge variant="outline" className={STATUS_COLOR[m.status] ?? ""}>
                        {m.status}
                      </Badge>
                      <Badge variant="secondary">{m.trigger}</Badge>
                      <Badge variant="outline">{m.instructorLabel ?? m.instructor}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 italic">"{m.transcript}"</p>
                    {m.conceptTags?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-2">
                        {m.conceptTags.map((t) => (
                          <span key={t} className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>
                        ))}
                      </div>
                    )}
                    {m.errorMessage && (
                      <p className="text-xs text-red-300 mt-2">Error: {m.errorMessage}</p>
                    )}
                    {m.updatedAt && (
                      <p className="text-xs text-muted-foreground mt-1">Last updated: {new Date(m.updatedAt).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 min-w-[170px]">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={m.status !== "ready" || playingId === m.momentId}
                      onClick={() => preview(m.momentId)}
                    >
                      <Play className="w-3.5 h-3.5 mr-1.5" />
                      {playingId === m.momentId ? "Playing…" : "Preview"}
                    </Button>
                    <Button
                      size="sm"
                      disabled={!!busy}
                      onClick={() => generateOne(m.momentId, false)}
                    >
                      {busy === m.momentId ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                      {m.status === "ready" ? "Re-check" : "Generate"}
                    </Button>
                    {m.status !== "missing" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!!busy}
                        onClick={() => generateOne(m.momentId, true)}
                      >
                        Force Regenerate
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
